import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, 
  Upload, 
  Calendar, 
  FileText,
  Building2,
  CreditCard,
  Copy,
  CheckCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PaymentProofUploader } from "@/components/PaymentSlipUploader";

interface UploadPaymentProofProps {
  base_path: string;
}

// Mock campaign data
const getCampaignData = (campaignId: string) => {
  return {
    id: parseInt(campaignId) || 16,
    name: "Pending Payment Campaign",
    code: "E - 2023-06070000016",
    startDate: "2024-01-15",
    endDate: "2024-02-15",
    totalPrice: 7500.00,
    subTotal: 6818.18,
    gst: 681.82,
    inventories: [
      {
        id: 3,
        name: "Sydney Harbor View",
        location: "New South Wales, Australia",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop"
      }
    ]
  };
};

export default function UploadPaymentProof({ base_path }: UploadPaymentProofProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [campaignData, setCampaignData] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    const campaignId = searchParams.get('campaign') || '16';
    const data = getCampaignData(campaignId);
    setCampaignData(data);
  }, [searchParams]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleGetUploadParameters = async () => {
    try {
      // TODO: Replace with actual GraphQL mutation when backend is ready
      // const response = await client.mutate({
      //   mutation: GET_UPLOAD_URL,
      //   variables: { campaignId: campaignData.id }
      // });
      
      // Mock response for now
      return {
        method: 'PUT' as const,
        url: '/api/payment-proofs/upload',
      };
    } catch (error: any) {
      toast({
        title: "Upload Failed", 
        description: error.message || "Failed to get upload URL. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleUploadComplete = (result: { uploadURL: string; filename: string }) => {
    toast({
      title: "Payment Proof Uploaded",
      description: "Your payment proof has been uploaded successfully.",
    });
    
    // Navigate back to payment method page or to review
    setTimeout(() => {
      navigate(`${base_path}/payment-method?campaign=${campaignData.id}`);
    }, 1500);
  };

  if (!campaignData) {
    return (
      <div className="w-full px-16 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-16 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate(`${base_path}/payment-method?campaign=${campaignData.id}`)}
          className="bg-white/80 backdrop-blur-sm border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Upload Payment Proof</h1>
          <p className="text-lg text-gray-600">Campaign: {campaignData.name}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-20">
          {/* Left Column - Upload Section */}
          <div className="flex-1 lg:w-3/4 space-y-6">
            {/* Payment Proof Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-emerald-600" />
                  Payment Proof Upload
                </CardTitle>
                <p className="text-sm text-gray-600">
                  Upload your payment proof for approval. Accepted formats: PDF, JPG, PNG (Max 10MB)
                </p>
              </CardHeader>
              <CardContent>
                <PaymentProofUploader
                  onGetUploadParameters={handleGetUploadParameters}
                  onComplete={handleUploadComplete}
                  buttonClassName="w-full"
                >
                  <div className="border-2 border-dashed border-emerald-300 rounded-lg p-8 text-center bg-emerald-50/30">
                    <Upload className="h-8 w-8 text-emerald-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-emerald-600 mb-2">Upload Payment Proof</h3>
                    <p className="text-emerald-600 mb-4">Click to select file or drag and drop</p>
                    <p className="text-sm text-emerald-500">Accepted formats: PDF, JPG, PNG (Max 10MB)</p>
                  </div>
                </PaymentProofUploader>
              </CardContent>
            </Card>

            {/* Supported Banks & Account Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                  Supported Banks & Account Details
                </CardTitle>
                <p className="text-sm text-gray-600">
                  Transfer payment to any of the following accounts with your campaign reference
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Commonwealth Bank */}
                <div className="border rounded-lg p-4 bg-gray-50/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Commonwealth Bank</h3>
                      <p className="text-sm text-gray-600">Australia's leading bank</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Account Name:</span>
                      <span className="font-medium">BillboardHub Pty Ltd</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">BSB:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">063-123</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText('063-123');
                            toast({ title: "Copied BSB to clipboard" });
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">1234 5678 90</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText('123456890');
                            toast({ title: "Copied account number to clipboard" });
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ANZ Bank */}
                <div className="border rounded-lg p-4 bg-gray-50/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">ANZ Bank</h3>
                      <p className="text-sm text-gray-600">Australia and New Zealand Banking</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Account Name:</span>
                      <span className="font-medium">BillboardHub Pty Ltd</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">BSB:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">012-345</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText('012-345');
                            toast({ title: "Copied BSB to clipboard" });
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">9876 5432 10</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText('987654321');
                            toast({ title: "Copied account number to clipboard" });
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Westpac Bank */}
                <div className="border rounded-lg p-4 bg-gray-50/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Westpac Bank</h3>
                      <p className="text-sm text-gray-600">Australia's first bank</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Account Name:</span>
                      <span className="font-medium">BillboardHub Pty Ltd</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">BSB:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">034-789</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText('034-789');
                            toast({ title: "Copied BSB to clipboard" });
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">5566 7788 99</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText('556677889');
                            toast({ title: "Copied account number to clipboard" });
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Reference Note */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-emerald-800 mb-1">Important Payment Reference</h4>
                      <p className="text-sm text-emerald-700">
                        Always include your campaign reference <strong>{campaignData.code}</strong> in the payment description to ensure quick processing.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Alternative Payment Methods */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Alternative Payment Methods</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 p-3 border rounded-lg bg-white">
                      <CreditCard className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">PayPal</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 border rounded-lg bg-white">
                      <CreditCard className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-medium">Payoneer</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Contact support for PayPal or Payoneer payment options
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Payment Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment Instructions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <h4 className="font-medium text-emerald-800 mb-2">Quick Bank Transfer Details</h4>
                    <div className="space-y-1 text-sm text-emerald-700">
                      <p><strong>Commonwealth Bank:</strong> BSB 063-123, Account 1234 5678 90</p>
                      <p><strong>ANZ Bank:</strong> BSB 012-345, Account 9876 5432 10</p>
                      <p><strong>Westpac Bank:</strong> BSB 034-789, Account 5566 7788 99</p>
                      <p><strong>Account Name:</strong> BillboardHub Pty Ltd</p>
                      <p><strong>Reference:</strong> {campaignData.code}</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600">•</span>
                      <span>Transfer <strong>{formatCurrency(campaignData.totalPrice)}</strong> to any of the above bank accounts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600">•</span>
                      <span>Always include campaign reference <strong>{campaignData.code}</strong> in payment description</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600">•</span>
                      <span>Upload clear photo/scan of payment receipt or bank transfer confirmation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600">•</span>
                      <span>Payment will be reviewed and approved within 24-48 hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600">•</span>
                      <span>Campaign goes live immediately after payment approval</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Payment Summary */}
          <div className="lg:w-1/4 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-gray-500">Campaign</div>
                  <div className="text-gray-900">{campaignData.name}</div>
                  <div className="text-sm text-gray-500">{campaignData.code}</div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>{formatDate(campaignData.startDate)} - {formatDate(campaignData.endDate)}</span>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>{formatCurrency(campaignData.subTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>GST</span>
                    <span>{formatCurrency(campaignData.gst)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total Amount</span>
                    <span className="text-emerald-600">{formatCurrency(campaignData.totalPrice)}</span>
                  </div>
                </div>

                {/* Billboard Preview */}
                {campaignData.inventories.map((inventory: any, index: number) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex gap-3">
                      <img 
                        src={inventory.image} 
                        alt={inventory.name}
                        className="w-16 h-12 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{inventory.name}</div>
                        <div className="text-xs text-gray-500">{inventory.location}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
    </div>
  );
}